<template>
  <v-container fluid class="pa-0 checklist-shell">
    <div v-if="isHr" class="checklist-actions mb-4">
      <v-btn color="secondary" variant="tonal" prepend-icon="mdi-repeat" @click="openRecurrenceDialog">
        {{ $t("checklist.actions.recurrenceRules") }}
      </v-btn>
      <v-btn color="tertiary" prepend-icon="mdi-plus" @click="openCreateDialog">
        {{ $t("checklist.actions.createTask") }}
      </v-btn>
    </div>

    <v-card class="filters-bar mb-3">
      <NcrmFilterPills
        :filters="pillFilters"
        :count-label="$t('checklist.filters.count', { count: tasks.length })"
        :clear-label="$t('checklist.filters.clear')"
        :has-active-filters="activeFilterCount > 0"
        :active-filter-count="activeFilterCount"
        @update="onPillUpdate"
        @clear="clearFilters"
      >
        <v-chip v-if="isHr" color="error" prepend-icon="mdi-alert-circle-outline">
          {{ $t("checklist.filters.overdue", { count: overdueCount }) }}
        </v-chip>
        <v-switch
          v-if="isHr"
          v-model="filters.includeClosed"
          class="ml-2"
          color="primary"
          hide-details
          density="compact"
          :label="$t('checklist.filters.includeClosed')"
          @update:model-value="fetchData"
        />
        <v-switch
          v-if="isHr"
          v-model="filters.employeeResolvedOnly"
          color="primary"
          hide-details
          density="compact"
          :label="$t('checklist.filters.employeeResolvedOnly')"
          @update:model-value="fetchData"
        />
      </NcrmFilterPills>
    </v-card>
    <v-alert
      v-if="errorMessage"
      type="error"
      density="compact"
      class="mb-3"
    >
      {{ errorMessage }}
    </v-alert>
    <!-- Eroarea de încărcare rămâne până la reîncercare; filtrele se păstrează. -->
    <AppErrorState v-if="loadError" class="mb-3" :message="$t('checklist.errors.load')" @retry="fetchData" />

    <v-row class="task-grid-row" dense>
      <v-col v-if="loading" cols="12">
        <v-skeleton-loader type="paragraph, paragraph, paragraph" />
      </v-col>
      <template v-else>
        <v-col v-for="task in tasks" :key="task.id" cols="12">
          <v-card
            :class="[taskClass(task), 'task-card compact-card', { 'task-card--clickable': isHr }]"
            @click="isHr && openEdit(task)"
          >
            <div class="compact-row top-line title-only">
              <div class="d-flex align-center ga-2 task-title-row">
                <v-icon size="18">mdi-checkbox-marked-circle-outline</v-icon>
                <button v-if="isHr" type="button" class="task-title task-title--button" @click.stop="openEdit(task)">
                  {{ task.title }}
                </button>
                <span v-else class="task-title">{{ task.title }}</span>
              </div>
            </div>
            <div class="compact-row info-row">
              <div class="chip-stack">
                <v-chip v-if="formatCategory(task.category_id)" size="x-small" color="primary">
                  {{ formatCategory(task.category_id) }}
                </v-chip>
                <v-chip size="x-small" :color="priorityColor(task.priority)">
                  {{ formatPriority(task.priority) }}
                </v-chip>
                <v-chip v-if="recurrenceChipLabel(task)" size="x-small" color="neutral">
                  {{ recurrenceChipLabel(task) }}
                </v-chip>
                <v-chip v-if="isOverdue(task)" size="x-small" color="error" prepend-icon="mdi-alert-circle-outline">
                  {{ $t("checklist.meta.overdue") }}
                </v-chip>
              </div>
              <div class="actions-side" @click.stop>
                <v-btn
                  v-if="canMarkDone(task)"
                  size="small"
                  color="primary"
                  @click="markDone(task)"
                  :loading="actionLoading === task.id + '-done'"
                >
                  {{ $t("checklist.actions.done") }}
                </v-btn>
                <v-btn
                  v-if="isHr && task.status !== 'closed'"
                  size="small"
                  variant="tonal"
                  color="success"
                  @click="closeTask(task)"
                  :loading="actionLoading === task.id + '-close'"
                >
                  {{ $t("checklist.actions.close") }}
                </v-btn>
              </div>
            </div>
            <div class="compact-row two-line bottom-line">
              <div class="text-caption text-medium-emphasis task-meta text-truncate">
                <v-menu
                  v-if="isHr"
                  :model-value="statusMenuTaskId === task.id"
                  @update:model-value="(val) => (statusMenuTaskId = val ? task.id : null)"
                  offset-y
                >
                  <template #activator="{ props: menuProps }">
                    <v-chip
                      v-bind="menuProps"
                      size="small"
                      :color="statusColor(task.status)"
                      class="clickable"
                      @click.stop
                    >
                      {{ formatStatus(task.status) }}
                    </v-chip>
                  </template>
                  <v-list density="compact">
                    <v-list-item
                      v-for="statusOpt in statusOptions"
                      :key="statusOpt.value"
                      @click="changeStatus(task, statusOpt.value)"
                    >
                      <v-list-item-title>{{ statusOpt.label }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-chip v-else :color="statusColor(task.status)" class="mr-3">
                  {{ formatStatus(task.status) }}
                </v-chip>
                <span class="meta-item">
                  <v-icon size="14" class="mr-1">mdi-calendar-clock</v-icon>
                  {{ dueDisplay(task.due_date) }}
                </span>
                <span v-if="task.closed_at" class="meta-item">
                  <v-icon size="14" class="mr-1">mdi-lock-check-outline</v-icon>
                  {{ $t("checklist.meta.closedAt") }}: {{ dueDisplay(task.closed_at) }}
                </span>
                <span v-if="task.assignees" class="assignee-inline">
                  <v-icon size="14" class="mr-1">mdi-account-multiple-outline</v-icon>
                  <strong>{{ formatAssigneeNames(task.assignees) }}</strong>
                </span>
                <span v-if="task.description" class="text-medium-emphasis one-line">
                  <v-icon size="14" class="mr-1">mdi-text</v-icon>
                  {{ task.description }}
                </span>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col v-if="!tasks.length && !loadError" cols="12">
          <AppEmptyState icon="mdi-checkbox-marked-circle-outline" :title="$t('checklist.empty')">
            <v-btn v-if="isHr" variant="outlined" color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
              {{ $t("checklist.actions.createTask") }}
            </v-btn>
          </AppEmptyState>
        </v-col>
      </template>
    </v-row>

    <NcrmRecordEditDialog
      v-model="createDialog"
      :title="editMode ? $t('checklist.dialog.editTitle') : $t('checklist.dialog.createTitle')"
      :subtitle="dialogSummary"
      :loading="saving"
      :save-label="editMode ? $t('app.actions.save') : $t('app.actions.create')"
      @save="createTask"
    >
      <v-alert v-if="taskFormError" type="error" density="compact" class="mb-3">{{ taskFormError }}</v-alert>
      <v-row dense>
        <v-col cols="12">
          <v-text-field v-model="form.title" :label="`${$t('checklist.form.title')} *`" hide-details="auto" />
        </v-col>
        <v-col cols="12">
          <v-textarea v-model="form.description" :label="$t('checklist.form.description')" rows="3" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-combobox
            v-model="form.category"
            :items="categoryItems"
            item-title="label"
            item-value="id"
            return-object
            clearable
            :label="$t('checklist.form.categoryPlaceholder')"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select v-model="form.priority" :items="priorityOptions" :label="$t('checklist.form.priority')" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="form.status"
            :items="statusOptions"
            item-title="label"
            item-value="value"
            :label="$t('checklist.form.status')"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6">
          <NcrmDateTimeField v-model="form.dueDate" :label="$t('checklist.form.dueDate')" :disabled="form.recurrenceEnabled" />
        </v-col>
        <v-col v-if="isHr" cols="12">
          <v-autocomplete
            v-model="form.assignees"
            :items="employees"
            item-title="fullName"
            item-value="id"
            :label="$t('checklist.form.employees')"
            multiple
            chips
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-switch v-model="form.visibleToEmployee" :label="$t('checklist.form.visibleToEmployee')" color="primary" hide-details />
        </v-col>
        <v-col v-if="isHr" cols="12" md="6">
          <v-switch v-model="form.recurrenceEnabled" :label="$t('checklist.form.recurrenceEnabled')" color="primary" hide-details />
        </v-col>
        <template v-if="form.recurrenceEnabled">
          <v-col cols="12" md="6">
            <v-select v-model="form.recurrenceFrequency" :items="recurrenceOptions" :label="$t('checklist.form.frequency')" hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.recurrenceStart" :label="$t('checklist.form.recurrenceStart')" type="date" hide-details />
          </v-col>
        </template>
      </v-row>
    </NcrmRecordEditDialog>

    <NcrmRecordEditDialog
      v-model="recurrenceDialog"
      :title="$t('checklist.recurrence.title')"
      :loading="savingRecurrence"
      :save-label="recurrenceForm.id ? $t('app.actions.save') : $t('app.actions.create')"
      :extra-action-label="$t('app.actions.reset')"
      extra-action-color="neutral"
      max-width="780"
      @save="saveRecurrence"
      @extra-action="resetRecurrenceForm"
    >
      <v-alert v-if="recurrenceError" type="error" density="compact" class="mb-3">{{ recurrenceError }}</v-alert>
      <div class="recurrence-list">
        <v-list>
          <v-list-item v-for="rule in recurrenceRules" :key="rule.id" :title="rule.title" :subtitle="recurrenceSubtitle(rule)">
            <template #append>
              <v-btn icon size="small" variant="text" :aria-label="$t('app.actions.edit')" :title="$t('app.actions.edit')" @click="openRecurrenceEdit(rule)">
                <v-icon size="16">mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn icon size="small" variant="text" color="error" :aria-label="$t('app.actions.delete')" :title="$t('app.actions.delete')" @click="askDeleteRecurrence(rule)">
                <v-icon size="16">mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-list-item>
          <v-list-item v-if="!recurrenceRules.length" :title="$t('checklist.recurrence.empty')" />
        </v-list>
      </div>
      <v-divider class="my-3"></v-divider>
      <div class="text-subtitle-2 mb-2">
        {{ recurrenceForm.id ? $t("checklist.recurrence.editTitle") : $t("checklist.recurrence.createTitle") }}
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field v-model="recurrenceForm.title" :label="`${$t('checklist.form.title')} *`" hide-details="auto" />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="recurrenceForm.frequency"
            :items="recurrenceOptions"
            item-title="title"
            item-value="value"
            :label="$t('checklist.form.frequency')"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="recurrenceForm.startDate" :label="`${$t('checklist.recurrence.startDate')} *`" type="date" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="recurrenceForm.endDate" :label="$t('checklist.recurrence.endDate')" type="date" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model.number="recurrenceForm.dueOffsetDays" :label="$t('checklist.recurrence.dueOffset')" type="number" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-select v-model="recurrenceForm.priority" :items="priorityOptions" :label="$t('checklist.form.priority')" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-combobox
            v-model="recurrenceForm.category"
            :items="categoryItems"
            item-title="label"
            item-value="id"
            return-object
            clearable
            :label="$t('checklist.form.categoryPlaceholder')"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-switch v-model="recurrenceForm.visibleToEmployee" :label="$t('checklist.form.visibleToEmployee')" color="primary" hide-details />
        </v-col>
      </v-row>
    </NcrmRecordEditDialog>

    <NcrmRecordDeleteDialog
      v-model="deleteRuleDialog"
      v-model:confirmText="deleteRuleText"
      :title="$t('checklist.recurrence.deleteTitle')"
      :hint="$t('checklist.recurrence.deleteHint', { title: deleteRuleTarget ? deleteRuleTarget.title : '' })"
      :confirm-label="$t('checklist.recurrence.deleteConfirmLabel')"
      :loading="deletingRule"
      @confirm="confirmDeleteRecurrence"
    />

  </v-container>
</template>

<script>
import axios from "axios";
import AppEmptyState from "@/components/app/AppEmptyState.vue";
import AppErrorState from "@/components/app/AppErrorState.vue";
import NcrmFilterPills from "@/components/ncrm/shared/NcrmFilterPills.vue";
import NcrmDateTimeField from "@/components/ncrm/shared/NcrmDateTimeField.vue";
import NcrmRecordDeleteDialog from "@/components/ncrm/shared/NcrmRecordDeleteDialog.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import { notifyMutation } from "@/Utile/mutationFeedback.mjs";

export default {
  name: "ChecklistPanel",
  components: { AppEmptyState, AppErrorState, NcrmFilterPills, NcrmDateTimeField, NcrmRecordDeleteDialog, NcrmRecordEditDialog },
  props: {
    mode: {
      type: String,
      default: "employee", // employee | hr
    },
    companyId: {
      type: Number,
      default: null,
    },
    employeeId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      actionLoading: null,
      createDialog: false,
      recurrenceDialog: false,
      recurrenceError: "",
      savingRecurrence: false,
      editMode: false,
      errorMessage: "",
      taskFormError: "",
      deleteRuleDialog: false,
      deleteRuleTarget: null,
      deleteRuleText: "",
      deletingRule: false,
      loadError: false,
      statusMenuTaskId: null,
      categories: [],
      employees: [],
      tasks: [],
      recurrenceRules: [],
      filters: {
        status: "",
        categoryId: null,
        employeeId: null,
        includeClosed: false,
        closedFrom: null,
        closedTo: null,
        recurrenceRuleId: null,
        employeeResolvedOnly: false,
      },
      form: this.emptyForm(),
      editingTaskId: null,
      recurrenceForm: {
        id: null,
        title: "",
        description: "",
        priority: "normal",
        category: null,
        visibleToEmployee: true,
        frequency: "daily",
        startDate: "",
        endDate: "",
        dueOffsetDays: 0,
      },
    };
  },
  computed: {
      isHr() {
        return this.mode === "hr";
      },
    statusOptions() {
      return [
        { value: "todo", label: this.$t("checklist.status.todo") },
        { value: "in_progress", label: this.$t("checklist.status.inProgress") },
        { value: "done", label: this.$t("checklist.status.done") },
        { value: "skipped", label: this.$t("checklist.status.skipped") },
        { value: "employee_resolved", label: this.$t("checklist.status.employeeResolved") },
        { value: "closed", label: this.$t("checklist.status.closed") },
      ];
    },
    recurrenceOptions() {
      return [
        { value: "daily", title: this.$t("checklist.recurrence.freq.daily") },
        { value: "weekly", title: this.$t("checklist.recurrence.freq.weekly") },
        { value: "monthly", title: this.$t("checklist.recurrence.freq.monthly") },
        { value: "quarterly", title: this.$t("checklist.recurrence.freq.quarterly") },
        { value: "yearly", title: this.$t("checklist.recurrence.freq.yearly") },
      ];
    },
    categoryItems() {
      return this.categories || [];
    },
    pillFilters() {
      const options = (items, valueKey, labelKey) => items.map((item) => ({ value: item[valueKey], label: item[labelKey] }));
      const labelOf = (list, value) => list.find((opt) => String(opt.value) === String(value))?.label || String(value ?? "");
      const list = [];
      const add = (field, type, label, opts, value, clearValue) =>
        list.push({
          field,
          type,
          label,
          options: opts,
          value,
          active: Boolean(value),
          display: opts && type === "select" ? labelOf(opts, value) : opts && type === "autocomplete" ? labelOf(options(opts, "id", "label"), value) : String(value ?? ""),
          clearValue,
        });
      add("status", "select", this.$t("checklist.form.status"), this.statusOptions, this.filters.status, "");
      add("categoryId", "select", this.$t("checklist.filters.category"), options(this.categoryItems, "id", "label"), this.filters.categoryId, null);
      if (this.isHr) {
        add("recurrenceRuleId", "select", this.$t("checklist.filters.recurrenceRule"), options(this.recurrenceRules, "id", "title"), this.filters.recurrenceRuleId, null);
        add("employeeId", "autocomplete", this.$t("checklist.filters.employee"), this.employees.map((e) => ({ id: e.id, label: e.fullName })), this.filters.employeeId, null);
        if (this.filters.includeClosed) {
          add("closedFrom", "date", this.$t("checklist.filters.closedFrom"), null, this.filters.closedFrom, null);
          add("closedTo", "date", this.$t("checklist.filters.closedTo"), null, this.filters.closedTo, null);
        }
      }
      return list;
    },
    activeFilterCount() {
      const pills = this.pillFilters.filter((f) => f.active).length;
      return pills + (this.filters.includeClosed ? 1 : 0) + (this.filters.employeeResolvedOnly ? 1 : 0);
    },
    overdueCount() {
      if (!this.isHr) return 0;
      return (this.tasks || []).filter((t) => this.isOverdue(t)).length;
    },
    dialogSummary() {
      return this.$t("checklist.dialog.summary", {
        status: this.formatStatus(this.form.status),
        due: this.form.dueDate ? this.dueDisplay(this.form.dueDate) : this.$t("checklist.values.noDue"),
      });
    },
    priorityOptions() {
      return ["low", "normal", "high"].map((value) => ({ value, title: this.$t(`checklist.priority.${value}`) }));
    },
    employeeNameMap() {
      return (this.employees || []).reduce((acc, emp) => {
        acc[emp.id] = emp.fullName || `ID ${emp.id}`;
        return acc;
      }, {});
    },
    categoryMap() {
      return (this.categories || []).reduce((acc, cat) => {
        acc[cat.id] = cat.label || `#${cat.id}`;
        return acc;
      }, {});
    },
      resolvedCompanyId() {
        if (this.companyId) return this.companyId;
        const raw = localStorage.getItem("user-company-id");
        const num = raw !== null && raw !== undefined ? Number(raw) : null;
        return Number.isFinite(num) ? num : null;
    },
    resolvedEmployeeId() {
      if (this.employeeId) return this.employeeId;
      const raw = localStorage.getItem("user-employee-id");
      const num = raw !== null && raw !== undefined ? Number(raw) : null;
      return Number.isFinite(num) ? num : null;
    },
  },
  mounted() {
    this.fetchData();
    if (this.isHr) {
      this.fetchEmployees();
      this.fetchRecurrenceRules();
      if (this.employeeId) {
        const parsed = Number(this.employeeId);
        this.filters.employeeId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      }
    }
  },
  watch: {
    employeeId(newVal) {
      if (!this.isHr) return;
      const parsed = Number(newVal);
      this.filters.employeeId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      this.fetchData();
    },
  },
  methods: {
    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        this.errorMessage = "";
      }, 4000);
    },
    recurrenceChipLabel(task) {
      if (!task || !task.recurrence_frequency) return null;
      const opt = this.recurrenceOptions.find((r) => r.value === task.recurrence_frequency);
      const freqLabel = opt ? opt.title : task.recurrence_frequency;
      return this.$t("checklist.recurrence.chip", { freq: freqLabel });
    },
    notify(text, color = "success") {
      notifyMutation(text, color);
    },
    // Valoarea de la server (ISO) -> „YYYY-MM-DDTHH:mm” local, formatul lui NcrmDateTimeField.
    toLocalInput(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    emptyForm() {
      return {
        title: "",
        description: "",
        priority: "normal",
        status: "todo",
        category: null,
        dueDate: "",
        assignees: [],
        visibleToEmployee: false,
        recurrenceEnabled: false,
        recurrenceFrequency: "daily",
        recurrenceStart: "",
      };
    },
    openEdit(task) {
      this.editingTaskId = task.id;
      this.editMode = true;
      this.taskFormError = "";
      this.form = {
        ...this.emptyForm(),
        title: task.title || "",
        description: task.description || "",
        priority: task.priority || "normal",
        status: task.status || "todo",
        category: task.category_id
          ? { id: task.category_id, label: this.categoryMap[task.category_id] || `#${task.category_id}` }
          : null,
        visibleToEmployee: !!task.visible_to_employee,
        assignees: task.assignees
          ? String(task.assignees)
              .split(",")
              .map((v) => Number(v.trim()))
              .filter((v) => Number.isFinite(v) && v > 0)
          : [],
        dueDate: this.toLocalInput(task.due_date),
      };
      this.createDialog = true;
    },
    buildDueIso() {
      if (!this.form.dueDate) return null;
      const d = new Date(this.form.dueDate);
      if (Number.isNaN(d.getTime())) throw new Error(this.$t("checklist.errors.invalidDueDate"));
      return d.toISOString();
    },
    openCreateDialog() {
      this.editMode = false;
      this.editingTaskId = null;
      this.taskFormError = "";
      this.form = this.emptyForm();
      this.createDialog = true;
    },
    async fetchData() {
      this.loading = true;
      this.loadError = false;
      try {
        const params = {
          companyId: this.resolvedCompanyId,
          status: this.filters.status || undefined,
          categoryId: this.filters.categoryId || undefined,
        };
        if (this.isHr) {
          params.includeClosed = this.filters.includeClosed;
          params.closedFrom = this.filters.closedFrom || undefined;
          params.closedTo = this.filters.closedTo || undefined;
          params.recurrenceRuleId = this.filters.recurrenceRuleId || undefined;
          params.employeeResolvedOnly = this.filters.employeeResolvedOnly || undefined;
          if (this.filters.employeeId) {
            params.employeeId = this.filters.employeeId;
          }
        } else if (this.resolvedEmployeeId) {
          params.employeeId = this.resolvedEmployeeId;
        }
        const [catRes, taskRes] = await Promise.all([
          axios.get("/checklist/categories", { params: { companyId: this.resolvedCompanyId } }),
          axios.get("/checklist/tasks", { params }),
        ]);
        this.categories = catRes?.data?.items || [];
        let fetchedTasks = taskRes?.data?.items || [];
    const parseIds = (raw) =>
      String(raw)
        .split(",")
        .map((v) => Number(v.trim()))
        .filter((v) => Number.isFinite(v) && v > 0);

        // Filtrare suplimentară:
        // - HR: poate filtra manual după angajat
        // - non-HR: filtrăm client-side să rămână doar task-urile în care e asignat
        if (this.isHr && this.filters.employeeId) {
          const empId = Number(this.filters.employeeId);
          const empName = this.employees.find((e) => e.id === empId)?.fullName || "";
          if (Number.isFinite(empId) && empId > 0) {
            fetchedTasks = fetchedTasks.filter((t) => {
              if (!t.assignees) return false;
              const ids = parseIds(t.assignees);
              const names = String(t.assignees)
                .split(",")
                .map((v) => v.trim().toLowerCase())
                .filter(Boolean);
              return ids.includes(empId) || (empName && names.includes(empName.toLowerCase()));
            });
          }
        } else if (!this.isHr) {
          const meId = this.resolvedEmployeeId;
          if (Number.isFinite(meId) && meId > 0) {
            fetchedTasks = fetchedTasks.filter((t) => {
              if (!t.assignees) return false;
              const ids = parseIds(t.assignees);
              return ids.includes(meId);
            });
          } else {
            fetchedTasks = [];
          }
        }
        this.tasks = fetchedTasks;
      } catch (error) {
        console.error("[checklist] fetchData failed", error);
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    async fetchEmployees() {
      if (!this.resolvedCompanyId) return;
      try {
        const { data } = await axios.get("/candidati/employees", {
          params: { company_id: this.resolvedCompanyId },
        });
        this.employees = (data || []).map((e) => ({
          id: e.id,
          fullName: [e.first_name, e.last_name].filter(Boolean).join(" ").trim() || `ID ${e.id}`,
        }));
      } catch (error) {
        console.error("[checklist] fetchEmployees failed", error);
      }
    },
    async fetchRecurrenceRules() {
      if (!this.isHr || !this.resolvedCompanyId) return;
      try {
        const { data } = await axios.get("/checklist/recurrence-rules", {
          params: { companyId: this.resolvedCompanyId },
        });
        // API-ul întoarce și regulile șterse (soft delete, `deleted_at`): nu se afișează.
        this.recurrenceRules = (data?.items || []).filter((rule) => !rule.deleted_at);
      } catch (error) {
        console.error("[checklist] fetchRecurrenceRules failed", error);
      }
    },
    openRecurrenceDialog() {
      this.recurrenceError = "";
      this.recurrenceDialog = true;
      this.resetRecurrenceForm();
      this.fetchRecurrenceRules();
    },
    recurrenceSubtitle(rule) {
      const freqLabel = (this.recurrenceOptions.find((r) => r.value === rule.frequency)?.title) || rule.frequency;
      const start = rule.start_date ? this.dueDisplay(rule.start_date) : "-";
      const end = rule.end_date ? this.dueDisplay(rule.end_date) : this.$t("checklist.values.noEnd");
      return this.$t("checklist.recurrence.subtitle", {
        freq: freqLabel,
        start,
        end,
        offset: rule.due_offset_days || 0
      });
    },
    openRecurrenceEdit(rule) {
      this.recurrenceForm = {
        id: rule.id,
        title: rule.title,
        description: rule.description,
        priority: rule.priority || "normal",
        category: rule.category_id ? { id: rule.category_id, label: this.categoryMap[rule.category_id] || "" } : null,
        visibleToEmployee: !!rule.visible_to_employee,
        frequency: rule.frequency || "daily",
        startDate: rule.start_date ? rule.start_date.slice(0, 10) : "",
        endDate: rule.end_date ? rule.end_date.slice(0, 10) : "",
        dueOffsetDays: rule.due_offset_days || 0,
      };
      this.recurrenceDialog = true;
    },
    resetRecurrenceForm() {
      this.recurrenceForm = {
        id: null,
        title: "",
        description: "",
        priority: "normal",
        category: null,
        visibleToEmployee: true,
        frequency: "daily",
        startDate: "",
        endDate: "",
        dueOffsetDays: 0,
      };
    },
    async saveRecurrence() {
      if (!this.recurrenceForm.title || !this.recurrenceForm.startDate) {
        this.recurrenceError = this.$t("checklist.errors.recurrenceMissing");
        return;
      }
      this.recurrenceError = "";
      this.savingRecurrence = true;
      try {
        const categoryId = await this.ensureCategoryId(this.recurrenceForm.category);
        const payload = {
          title: this.recurrenceForm.title,
          description: this.recurrenceForm.description,
          priority: this.recurrenceForm.priority,
          categoryId,
          visibleToEmployee: this.recurrenceForm.visibleToEmployee,
          frequency: this.recurrenceForm.frequency,
          startDate: this.recurrenceForm.startDate,
          endDate: this.recurrenceForm.endDate || null,
          dueOffsetDays: this.recurrenceForm.dueOffsetDays || 0,
          companyId: this.resolvedCompanyId,
        };
        if (this.recurrenceForm.id) {
          await axios.patch(`/checklist/recurrence-rules/${this.recurrenceForm.id}`, payload);
        } else {
          await axios.post("/checklist/recurrence-rules", payload);
        }
        await this.fetchRecurrenceRules();
        this.resetRecurrenceForm();
        this.recurrenceDialog = false;
        this.notify(this.$t("checklist.feedback.recurrenceSaved"));
      } catch (error) {
        console.error("[checklist] saveRecurrence failed", error);
        this.recurrenceError =
          error?.response?.data?.message || error?.message || this.$t("checklist.errors.saveRecurrence");
      } finally {
        this.savingRecurrence = false;
      }
    },
    askDeleteRecurrence(rule) {
      this.deleteRuleTarget = rule;
      this.deleteRuleText = "";
      this.deleteRuleDialog = true;
    },
    async confirmDeleteRecurrence() {
      if (!this.deleteRuleTarget) return;
      this.deletingRule = true;
      try {
        await axios.delete(`/checklist/recurrence-rules/${this.deleteRuleTarget.id}`);
        this.deleteRuleDialog = false;
        await this.fetchRecurrenceRules();
        this.notify(this.$t("checklist.feedback.recurrenceDeleted"));
      } catch (error) {
        console.error("[checklist] deleteRecurrence failed", error);
        this.deleteRuleDialog = false;
        this.recurrenceError = error?.response?.data?.message || this.$t("checklist.errors.deleteRecurrence");
      } finally {
        this.deletingRule = false;
        this.deleteRuleText = "";
      }
    },
    onPillUpdate({ field, value }) {
      if (field === "employeeId") {
        this.onEmployeeFilterChange(value);
        return;
      }
      this.filters[field] = value || (field === "status" ? "" : null);
      this.fetchData();
    },
    clearFilters() {
      this.filters = {
        status: "",
        categoryId: null,
        employeeId: null,
        includeClosed: false,
        closedFrom: null,
        closedTo: null,
        recurrenceRuleId: null,
        employeeResolvedOnly: false,
      };
      this.fetchData();
    },
    onEmployeeFilterChange(val) {
      const parsed = Number(val);
      this.filters.employeeId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      this.fetchData();
    },
    dueDisplay(due) {
      if (!due) return this.$t("checklist.values.noDue");
      const d = new Date(due);
      if (Number.isNaN(d.getTime())) return this.$t("checklist.values.noDue");
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const mins = String(d.getMinutes()).padStart(2, "0");
      return `${day}.${month}.${year}, ${hours}:${mins}`;
    },
    priorityColor(priority) {
      const p = String(priority || "").toLowerCase();
      if (p === "high") return "error";
      if (p === "low") return "info";
      return "primary";
    },
    statusColor(status) {
      const s = String(status || "").toLowerCase();
      if (s === "closed") return "success";
      if (s === "in_progress") return "info";
      if (s === "done") return "primary";
      if (s === "employee_resolved") return "success";
      if (s === "skipped") return "warning";
      return "neutral";
    },
    formatPriority(priority) {
      const p = String(priority || "normal").toLowerCase();
      return ["low", "normal", "high"].includes(p) ? this.$t(`checklist.priority.${p}`) : p;
    },
    formatStatus(status) {
      if (!status) return "";
      const s = String(status).toLowerCase();
      const match = (this.statusOptions || []).find((opt) => opt.value === s);
      return match ? match.label : s.charAt(0).toUpperCase() + s.slice(1).replace("_", " ");
    },
    formatAssigneeNames(value) {
      if (!value) return "";
      const map = this.employeeNameMap || {};
      const ids = Array.isArray(value)
        ? value
        : String(value)
            .split(",")
            .map((v) => Number(v.trim()))
            .filter((v) => Number.isFinite(v) && v > 0);
      if (!ids.length) return "";
      // Non-HR: nu afișăm ID-uri; dacă utilizatorul curent e asignat, afișăm „Tu”.
      if (!this.isHr) {
        const meId = this.resolvedEmployeeId;
        if (Number.isFinite(meId) && ids.includes(meId)) {
          return this.$t("checklist.values.you");
        }
        return "";
      }
      const names = ids.map((id) => map[id] || `ID ${id}`);
      return names.join(", ");
    },
    formatCategory(categoryId) {
      if (!categoryId) return "";
      const label = this.categoryMap?.[categoryId];
      if (!label) return "";
      // ascundem fallback-uri numerice (#3 etc.)
      if (/^#?\d+$/.test(label.trim())) return "";
      return label;
    },
    isOverdue(task) {
      if (!task || !task.due_date) return false;
      const due = new Date(task.due_date);
      return due < new Date() && !["closed", "employee_resolved"].includes(task.status);
    },
    taskClass(task) {
      return this.isOverdue(task) ? "overdue-task" : "";
    },
    canMarkDone(task) {
      return task.status !== "closed" && !this.isHr;
    },
    async markDone(task) {
      this.actionLoading = `${task.id}-done`;
      try {
        await axios.post(`/checklist/tasks/${task.id}/mark-done`);
        await this.fetchData();
      } catch (error) {
        console.error("[checklist] markDone failed", error);
        this.showError(this.$t("checklist.errors.markDone"));
      } finally {
        this.actionLoading = null;
      }
    },
    async changeStatus(task, newStatus) {
      if (!this.isHr) return;
      this.statusMenuTaskId = null;
      try {
        await axios.patch(`/checklist/tasks/${task.id}`, { status: newStatus });
        await this.fetchData();
      } catch (error) {
        console.error("[checklist] changeStatus failed", error);
        this.showError(this.$t("checklist.errors.changeStatus"));
      }
    },
    async closeTask(task) {
      this.actionLoading = `${task.id}-close`;
      try {
        await axios.post(`/checklist/tasks/${task.id}/close`);
        await this.fetchData();
      } catch (error) {
        console.error("[checklist] closeTask failed", error);
        this.showError(this.$t("checklist.errors.closeTask"));
      } finally {
        this.actionLoading = null;
      }
    },
    async ensureCategoryId(categoryInput) {
      if (!categoryInput) return null;
      if (!this.resolvedCompanyId) {
        throw new Error(this.$t("checklist.errors.selectCompany"));
      }
      if (typeof categoryInput === "object" && categoryInput.id) {
        return categoryInput.id;
      }
      if (typeof categoryInput === "number") {
        return categoryInput;
      }
      const label = String(categoryInput).trim();
      if (!label) return null;
      const existing = this.categories.find((c) => c.label.toLowerCase() === label.toLowerCase());
      if (existing) return existing.id;
      try {
        const { data } = await axios.post("/checklist/categories", {
          label,
          companyId: this.resolvedCompanyId,
        });
        const newId = data?.id;
        if (newId) {
          this.categories.push({ id: newId, label });
        }
        return newId || null;
      } catch (error) {
        if (error?.response?.status === 409) {
          // categorie deja exista: încearcă să o găsești sau refetch rapid
          let dup = this.categories.find((c) => c.label.toLowerCase() === label.toLowerCase());
          if (!dup) {
            const { data: refetched } = await axios.get("/checklist/categories", {
              params: { companyId: this.resolvedCompanyId },
            });
            this.categories = refetched?.items || [];
            dup = this.categories.find((c) => c.label.toLowerCase() === label.toLowerCase());
          }
          if (dup) return dup.id;
          return null;
        }
        this.showError(error?.response?.data?.message || this.$t("checklist.errors.createCategory"));
        throw error;
      }
    },
    async createTask() {
      this.saving = true;
      this.taskFormError = "";
      try {
        if (!this.resolvedCompanyId) {
          throw new Error(this.$t("checklist.errors.selectCompany"));
        }
        if (!String(this.form.title || "").trim()) {
          throw new Error(this.$t("checklist.errors.titleRequired"));
        }
        const assignees = (this.form.assignees || []).map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0);
        const categoryId = await this.ensureCategoryId(this.form.category);

        if (this.form.recurrenceEnabled) {
          if (!this.isHr) {
            throw new Error(this.$t("checklist.errors.onlyHrRecurrence"));
          }
          if (!this.form.recurrenceStart) {
            throw new Error(this.$t("checklist.errors.recurrenceStart"));
          }
          await axios.post("/checklist/recurrence-rules", {
            title: this.form.title,
            description: this.form.description,
            priority: this.form.priority,
            categoryId,
            visibleToEmployee: this.form.visibleToEmployee,
            frequency: this.form.recurrenceFrequency,
            startDate: this.form.recurrenceStart,
            endDate: null,
            dueOffsetDays: 0,
            companyId: this.resolvedCompanyId,
          });
        } else {
          const body = {
            title: this.form.title,
            description: this.form.description,
            priority: this.form.priority,
            status: this.form.status,
            categoryId,
            visibleToEmployee: this.form.visibleToEmployee,
            dueDate: this.buildDueIso(),
            assignees,
          };
          if (this.editMode && this.editingTaskId) {
            await axios.patch(`/checklist/tasks/${this.editingTaskId}`, body);
          } else {
            await axios.post("/checklist/tasks", { ...body, companyId: this.resolvedCompanyId });
          }
        }
        this.createDialog = false;
        this.editMode = false;
        this.editingTaskId = null;
        this.form = this.emptyForm();
        this.notify(this.$t("checklist.feedback.taskSaved"));
        await this.fetchData();
      } catch (error) {
        console.error("[checklist] createTask failed", error);
        this.taskFormError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          this.$t("checklist.errors.createTask");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.overdue-task {
  border-left: 3px solid rgb(var(--v-theme-error));
}
.recurrence-list {
  max-height: 320px;
  overflow: auto;
}
.filters-bar {
  padding: 8px 12px;
}
.task-grid-row {
  row-gap: 10px;
}
.task-card {
  padding-top: 4px;
}
.compact-card {
  padding: 8px 12px;
}
.compact-row {
  display: grid;
  grid-template-columns: 1fr 0.35fr auto;
  gap: 8px;
  align-items: center;
}
.two-line {
  padding: 4px 0;
}
.top-line {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}
.bottom-line {
  padding-top: 6px;
}
.task-card .v-card-title {
  padding: 6px 12px;
}
.task-card .v-card-text {
  padding: 4px 12px;
  }
.task-card .v-card-actions {
  padding: 4px 12px;
}
.title-only {
  border-bottom: none;
}
.info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  padding-top: 4px;
  padding-bottom: 4px;
}
.task-meta {
  display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
.task-title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--app-text-strong);
  display: block;
  line-height: 1.2;
}
.task-title-row {
  width: 100%;
  align-items: flex-start;
}
  .one-line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
.clickable {
  cursor: pointer;
}
.chip-stack {
  gap: 6px;
}

.checklist-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
@media (max-width: 768px) {
  .checklist-actions {
    justify-content: flex-start;
  }
}
.task-card--clickable {
  cursor: pointer;
  transition: border-color var(--app-transition-fast), background-color var(--app-transition-fast);
}
.task-card--clickable:hover {
  border-color: var(--ncrm-color-primary-border);
  background-color: var(--ncrm-color-hover-soft);
}
.task-card--clickable:focus-within {
  border-color: var(--ncrm-color-primary-border);
}
.task-title--button {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
}
.task-title--button:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: 2px;
  border-radius: var(--app-radius-sm);
}

.actions-side {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  align-items: center;
}
.assignee-inline {
  font-size: 12px;
  font-weight: 600;
  color: var(--app-text-strong);
  display: inline-flex;
  align-items: center;
}
</style>
